import React, { useState, useRef, useEffect } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { process } from "@progress/kendo-data-query";
import { useNavigate } from "react-router-dom";
import { getActiveCurrency } from "actions/currency.action.js";
import { useDispatch } from "react-redux";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import AddNewSupplier from "./AddNewSupplier.jsx";
import { AmountFormatCell } from "components/common/formatter";
import SearchTextBox from "components/SearchComponent.jsx";
import styles from "./Supplier.module.scss";
import { RedirectCell } from "components/common/GridComponents.jsx";
import { GridLoader } from "components/GridLoader.jsx";

import { fetchSuppliersData } from "services/supplierService";

const Suppliers = ({ type }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchRef = useRef();
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [viewType, setViewType] = useState(null);
    const [visible, setVisible] = useState(false);
    const [value, setValue] = useState(undefined);
    const [archiveChecked, setArchiveChecked] = useState(true);
    const [includeZeroChecked, setIncludeZeroChecked] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const [supplierData, setSupplierData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataState, setDataState] = useState({
        take: 50,
        skip: 0,
        sort: [{ field: "supplierRef", dir: "asc" }],
    });

    const [dataResult, setDataResult] = useState(process([], dataState));

    const editorId = "firstName";
    const toggleDialog = () => setVisible(!visible);

    useEffect(() => {
        fetchSuppliers();
    }, [archiveChecked, includeZeroChecked, searchValue, type]);

    useEffect(() => {
        setDataResult(process(supplierData, dataState));
    }, [supplierData, dataState]);

    useEffect(() => {
        dispatch(getActiveCurrency());
    }, [dispatch]);

    const fetchSuppliers = async () => {
        setIsLoading(true);
        const params = {};
        if (archiveChecked) params.excludeArchived = true;
        if (includeZeroChecked) params.excludeZeroBalance = true;
        if (searchValue) params.search = searchValue;
        if (type === "Consultant") params.type = "Consultant";

        const suppliers = await fetchSuppliersData(params);
        setSupplierData(suppliers || []);
        setIsLoading(false);
    };

    const handleAction = async (supplierId) => {
        setSelectedSupplierId(supplierId);
        setViewType("View");
    };

    const handleRefresh = () => {
        setSearchValue("");
        searchRef.current?.clearSearch();
        setDataState((prev) => ({ ...prev, skip: 0 }));
        fetchSuppliers();
    };

    const Redirect = ({ dataItem }) => (
        <RedirectCell handleClick={() => handleAction(dataItem.id)} />
    );

    const LedgerCell = (props) => {
        const supplierRef = props.dataItem?.supplierRef;
        return (
            <td>
                <span
                    className="cursor-pointer"
                    onClick={() => {
                        const path =
                            props.type === "Consultant"
                                ? "/consultant/consultantledger"
                                : "/purchase/supplierledger";

                        navigate(`${path}?supplierRef=${encodeURIComponent(supplierRef)}`);
                    }}
                >
                    <u>{supplierRef}</u>
                </span>
            </td>
        );
    };

    const children = visible && (
        <Dialog
            title="Please confirm"
            onClose={toggleDialog}
            className="mattersDialog"
        >
            <h1 style={{ marginBottom: "20px", fontWeight: 500 }}>Posting Details</h1>
            <div className="mattersDialogField">
                <div className="label">
                    <Label editorId={editorId}>Date&nbsp;</Label>
                </div>
                <span>11/05/2022</span>
            </div>
            <div className="mattersDialogField">
                <div className="label">
                    <Label editorId={editorId}>Ref&nbsp;</Label>
                </div>
                <span>9872001</span>
            </div>
            <div className="mattersDialogField">
                <div className="label">
                    <Label editorId={editorId}>Details&nbsp;</Label>
                </div>
                <span>Payment to National Bridges</span>
            </div>
            <div className="mattersDialogField">
                <div className="label">
                    <Label editorId={editorId}>Net Amount&nbsp;</Label>
                </div>
                <span>90.00</span>
            </div>
            <div className="mattersDialogField">
                <div className="label" style={{ marginTop: "7px" }}>
                    <Label editorId={editorId}>Notes&nbsp;</Label>
                </div>
                <Input
                    id={editorId}
                    value={value}
                    onChange={(e) => setValue(e.value)}
                />
            </div>
            <div className="mattersDialogButtons">
                <button className="k-button reverseBtn" onClick={toggleDialog}>
                    REVERSE
                </button>
                <button className="k-button editBtn" onClick={toggleDialog}>
                    EDIT
                </button>
            </div>
        </Dialog>
    );

    return (
        <div className="page h-100 d-flex flex-column">

            <div className="grid-layout-container" style={{ marginBottom: 20 }}>
                <div className="title menu-button flex justify-space-between align-items-center">
                    <div className="d-flex">
                        <SearchTextBox ref={searchRef} changeFilterValue={setSearchValue} />
                        <Button
                            className="ms-2 border-0 common-btn"
                            svgIcon={refreshIcon}
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                        <div
                            className="ps-4"
                            style={{
                                flexDirection: "column",
                                display: "flex",
                                fontSize: 15,
                                fontWeight: "500",
                            }}
                        >
                            <Checkbox
                                checked={includeZeroChecked}
                                onChange={() => setIncludeZeroChecked(!includeZeroChecked)}
                                label={"Exclude Zero Balances"}
                            />
                            <Checkbox
                                checked={archiveChecked}
                                onChange={() => setArchiveChecked(!archiveChecked)}
                                label={"Exclude Archived"}
                            />
                        </div>
                    </div>
                    {!type && (
                        <div>
                            <Button className="common-btn" onClick={() => setViewType("Add")}>
                                Add Supplier
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {children}

            {viewType !== null && (
                <AddNewSupplier
                    viewType={viewType}
                    supplierId={selectedSupplierId}
                    refresh={handleRefresh}
                    type={type}
                    onClose={() => {
                        setViewType(null);
                        setSelectedSupplierId(null);
                    }}
                />
            )}

            <Grid
                data={dataResult}
                className="flex-grow-1 overflow-auto grid-minwidth-col2-200 grid-minwidth-col4-200"
                sortable={true}
                onDataStateChange={(e) => {
                    setDataState(e.dataState);
                    setDataResult(process(supplierData, e.dataState));
                }}
                {...dataState}
                pageable={{
                    pageSizes: [10, 20, 50, 100],
                }}
                resizable={true}
                showLoader={isLoading}
                loader={<GridLoader />}
            >
                <GridNoRecords>
                    {isLoading ? (
                        <div style={{ height: "110px" }}></div>
                    ) : (
                        dataResult.data && dataResult.data.length === 0 && "No records available"
                    )}
                </GridNoRecords>
                <GridColumn
                    field="supplierRef"
                    title="Ref"
                    width="140px"
                    cell={(props) => <LedgerCell {...props} type={type} />}
                />
                <GridColumn
                    field="supplierName"
                    title={type === "Consultant" ? "Consultant Name" : "Supplier Name"}
                    className="gridBoldField"
                />
                <GridColumn field="currencyCode" title="Currency" width="100px" />
                <GridColumn field="payeeName" title="Payee Name" />
                <GridColumn field="transactionTypes" title="Type" width="120px" />
                <GridColumn
                    field="balance"
                    title="Balance"
                    className="balanceCol balanceTd"
                    cell={AmountFormatCell}
                    width="120px"
                    headerClassName="grid-header-center"
                />
                {archiveChecked && (
                    <GridColumn field="archived" title="Archived" width="90px" />
                )}
                <GridColumn width="60px" cell={Redirect} sortable={false} />
            </Grid>
        </div>
    );
};

export default Suppliers;
